import { useEffect, useState } from 'react';
import { useTrail, animated } from '@react-spring/web';
import { Lightbox } from './Lightbox';

interface ArtworkData {
  id: number;
  thumbnail: string;
  carouselImages: string[];
  caption: {
    title: string;
    location?: string;
    year: string;
  };
}

const asTheDayBurnsBaseUrl =
  'https://assets.salieflewis.com/as_the_day_burns/webp';
const asTheDayBurnsImages = Array.from(
  { length: 15 },
  (_, index) => `${asTheDayBurnsBaseUrl}/as_the_day_burns_${15 - index}.webp`,
);

function preloadImages(urls: string[]) {
  for (const url of urls) {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  }
}

const artworks: ArtworkData[] = [
  {
    id: 1,
    thumbnail: asTheDayBurnsImages[0],
    carouselImages: asTheDayBurnsImages,
    caption: {
      title: 'As the Day Burns',
      year: '2023-2026',
    },
  },
];

export function ArtworkDetail() {
  const [show, setShow] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState<ArtworkData | null>(
    null,
  );
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    setShow(false);
    const timer = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const warmInitialImages = () =>
      preloadImages(asTheDayBurnsImages.slice(0, 3));

    if ('requestIdleCallback' in window) {
      const idleCallback = window.requestIdleCallback(warmInitialImages);
      return () => window.cancelIdleCallback(idleCallback);
    }

    const timer = window.setTimeout(warmInitialImages, 600);
    return () => window.clearTimeout(timer);
  }, []);

  const imageTrail = useTrail(artworks.length, {
    from: { opacity: 0 },
    to: show ? { opacity: 1 } : { opacity: 0 },
    config: { duration: 250 },
  });

  const handleImageClick = (artwork: ArtworkData) => {
    setSelectedArtwork(artwork);
    setCurrentImageIndex(0);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setSelectedArtwork(null);
    setCurrentImageIndex(0);
  };

  const handleNavigate = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row gap-8 md:gap-4 p-0 items-center justify-center bg-transparent relative w-full z-1">
        <div className="flex max-w-full flex-col items-center justify-center gap-10 p-4 md:flex-row md:gap-8">
          {artworks.map((artwork, index) => {
            return (
              <animated.div
                key={artwork.id}
                className="relative z-1 w-[min(82vw,640px)] shrink-0 origin-center cursor-pointer transition-transform duration-500 ease-out md:w-[min(62vw,760px)] md:hover:scale-[1.02]"
                style={imageTrail[index]}
                onClick={() => handleImageClick(artwork)}
              >
                <div className="p-0">
                  <div className="p-0">
                    <img
                      src={artwork.thumbnail}
                      alt={artwork.caption.title}
                      width={3024}
                      height={4032}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="block h-auto w-full object-contain md:max-h-[min(58vh,680px)] xl:max-h-[720px]"
                    />
                  </div>
                </div>
              </animated.div>
            );
          })}
        </div>
      </div>

      {lightboxOpen && selectedArtwork && (
        <Lightbox
          images={selectedArtwork.carouselImages}
          caption={selectedArtwork.caption}
          currentIndex={currentImageIndex}
          onClose={handleCloseLightbox}
          onNavigate={handleNavigate}
        />
      )}
    </>
  );
}
